import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the raw text of each QR read, or of the typed / pasted code when the user presses Use. */
  onScan: (text: string) => void;
  title?: string;
  /** Keep the camera running after a read (asset counts scan many assets in a row). */
  continuous?: boolean;
  /** Label of the typed-code box, which also takes a USB scanner (it types the code and presses Enter). */
  manualLabel?: string;
  /** Extra controls or feedback shown under the code box (for example a result line for the last scan). */
  children?: React.ReactNode;
}

/** The same code twice within this window is one scan, not two (the camera sees a label for many frames). */
const REPEAT_WINDOW_MS = 2500;

/**
 * The one scanner dialog every QR flow shares: camera on top, a typed-code box below. The camera needs a secure page
 * (https or localhost) and the user's permission; when it is unavailable the box still works, so a scan never dead-ends.
 */
const ScanDialog: React.FC<Props> = ({
  open, onClose, onScan, title = 'Scan a QR code', continuous = false, manualLabel = 'Or type the code', children,
}) => {
  // A callback ref, so the effect re-runs once MUI has mounted the <video> (the dialog content mounts after open).
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const last = useRef<{ text: string; at: number } | null>(null);
  const seen = useRef<Set<string>>(new Set());
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const [already, setAlready] = useState<string | null>(null);

  // Fresh state for each opening. Kept apart from the camera effect: that one re-runs when the <video> mounts or
  // unmounts, and resetting the error there would remount the video again in a loop.
  useEffect(() => {
    if (!open) return;
    setCameraError(null);
    setManual('');
    setAlready(null);
    last.current = null;
    seen.current = new Set();
  }, [open, continuous]);

  useEffect(() => {
    if (!open) return undefined;
    let controls: IScannerControls | undefined;
    let stopped = false;

    const unavailable = !navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function';
    if (unavailable) {
      setCameraError('The camera is not available on this page. Type the code below instead.');
      return undefined;
    }
    if (!video) return undefined;
    const start = async () => {
      try {
        const reader = new BrowserMultiFormatReader();
        const c = await reader.decodeFromVideoDevice(undefined, video, (result) => {
          if (!result || stopped) return;
          const text = result.getText();
          const now = Date.now();
          if (last.current && last.current.text === text && now - last.current.at < REPEAT_WINDOW_MS) return;
          last.current = { text, at: now };
          if (continuous) {
            // One physical asset is counted once per session; a label seen again only gets a quiet note.
            if (seen.current.has(text)) { setAlready(text); return; }
            seen.current.add(text);
            setAlready(null);
          }
          onScanRef.current(text);
          if (!continuous) {
            stopped = true;
            controls?.stop();
          }
        });
        if (stopped) c.stop(); else controls = c;
      } catch {
        if (!stopped) setCameraError('Could not start the camera. Allow camera access, or type the code below.');
      }
    };
    start();
    return () => {
      stopped = true;
      controls?.stop();
    };
  }, [open, continuous, video]);

  const submitManual = () => {
    const text = manual.trim();
    if (!text) return;
    onScan(text);
    setManual('');
  };

  return (
    <Dialog data-help-page="store.scan.dialog" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {cameraError ? (
          <Alert severity="info" sx={{ mb: 2 }}>{cameraError}</Alert>
        ) : (
          <Box sx={{ mb: 2, bgcolor: 'common.black', borderRadius: 1, overflow: 'hidden', lineHeight: 0 }}>
            <video ref={setVideo} data-testid="scan-video" muted playsInline style={{ width: '100%', maxHeight: 320 }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            data-help="store.scan.manual" label={manualLabel} size="small" value={manual} autoFocus={!!cameraError}
            onChange={e => setManual(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitManual(); } }}
            sx={{ flex: '1 1 220px', maxWidth: 360 }}
            inputProps={{ 'data-testid': 'scan-manual' }}
          />
          <Button data-help="store.scan.use" variant="outlined" onClick={submitManual} disabled={!manual.trim()} sx={{ mt: 0.25 }}>Use</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Point the camera at a label. A USB scanner works in the box above.
        </Typography>
        {already && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }} data-testid="scan-already">
            Already counted in this session.
          </Typography>
        )}
        {children}
      </DialogContent>
      <DialogActions>
        <Button data-help="store.scan.close" onClick={onClose}>{continuous ? 'Done' : 'Cancel'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanDialog;
